import { CSSProperties, ReactNode, useLayoutEffect } from "react";
import classnames from "classnames";
import { motion } from "motion/react";

import DocumentTitle from "../../functional-components/DocumentTitle";
import { useUser } from "../../functional-components/UserContext";
import RobotsNoindex from "../../components/RobotsNoindex";
import Footer from "../Footer";
import FooterBottom from "../FooterBottom";
import { useLocation } from "react-router-dom";
import PrerenderReady from "../../components/PrerenderReady";

const motionVariants = {
  simple: {
    initial: {
      opacity: 0,
      zIndex: 1
    },
    in: {
      opacity: 1,
      zIndex: 1,
      transition: {
        type: "tween",
        ease: "anticipate",
        duration: 2.3
      }
    },
    out: {
      opacity: 0,
      zIndex: -1,
      transition: {
        type: "tween",
        ease: "anticipate",
        duration: 1
      }
    }
  },
  zoom: {
    initial: {
      opacity: 0,
      scale: 1.25,
      zIndex: 1
    },
    in: {
      opacity: 1,
      scale: 1,
      zIndex: 1
    },
    out: {
      opacity: 0,
      zIndex: -1,
      transition: {
        duration: 0,
        delay: 2
      }
    }
  },
  none: {
    initial: {},
    in: {},
    out: {}
  }
};

const Page = ({
  title,
  name,
  transition = "zoom",
  displayAsOverlay = false,
  noFooter = false,
  nonSrollable = false,
  scrollToTop = "yes",
  isInternal = false,
  noindex = false,
  children,
  style = {}
}: {
  title: string;
  name: string;
  transition?: "simple" | "zoom" | "none";
  displayAsOverlay?: boolean;
  noFooter?: boolean;
  nonSrollable?: boolean;
  scrollToTop?: "yes" | "smooth" | "no";
  isInternal?: boolean;
  noindex?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
}) => {
  const location = useLocation();
  const { b2b } = useUser();

  useLayoutEffect(() => {
    if (scrollToTop !== "no") {
      //console.log("scrollToTop");
      document.documentElement.scrollTo({
        top: 0,
        left: 0,
        behavior: scrollToTop === "smooth" ? "smooth" : "instant"
      });
    }
  }, [location.pathname, scrollToTop]);

  const ua = navigator.userAgent;
  const isFirefox = ua.indexOf("Firefox") > -1;

  if (displayAsOverlay || isInternal || isFirefox) transition = "simple";

  console.log("Page render:", title);

  return transition !== "none" ? (
    <motion.main
      className={classnames("page", name, { "non-scrollable": nonSrollable })}
      style={{
        transformOrigin: "50vw 50vh",
        //marginTop: "var(--padding-top)",
        ...(b2b && { top: "var(--menu-height)" }), //prevent page from being covered by the menu bar
        ...style
      }}
      initial="initial"
      animate="in"
      exit="out"
      variants={motionVariants[transition] || motionVariants["zoom"]}
      transition={{
        type: "spring",
        bounce: 0.15,
        duration: 0.5
      }}
    >
      {!!noindex && <RobotsNoindex />}
      <DocumentTitle title={title} />
      {children}
      {!(noFooter || nonSrollable || displayAsOverlay || isInternal) && (
        <>
          <Footer />
          <FooterBottom />
        </>
      )}
      <PrerenderReady />
    </motion.main>
  ) : (
    <div
      className={classnames("page", name, { "non-scrollable": nonSrollable })}
      style={{
        ...(b2b && { top: "var(--menu-height)" }), //prevent page from being covered by the menu bar
        ...style
      }}
    >
      {!!noindex && <RobotsNoindex />}
      <DocumentTitle title={title} />
      {children}
      {!(noFooter || nonSrollable || displayAsOverlay || isInternal) && (
        <>
          <Footer />
          <FooterBottom />
        </>
      )}
      <PrerenderReady />
    </div>
  );
};

export default Page;
